/**
 * VoucherRegister.tsx — Tally-grade voucher register for AURA v2.
 *
 * One row per Voucher with filter chips for the 20 voucher types, FY-aware
 * date presets, account / party autocomplete, status checkboxes, sortable
 * sticky-header table, drawer drill-down, footer sums and CSV/PDF/print
 * export. Keyboard nav (J/K/F/Enter/Esc) for CA muscle memory.
 *
 * READ-ONLY surface: voucher creation, posting and cancellation continue to
 * live in the existing AccountingPage flows; the register only navigates.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import {
  Search,
  Filter,
  Calendar,
  RefreshCw,
  Download,
  Printer,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  X,
} from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import { formatINR, formatDate, getFinancialYear } from '@/utils/formatters';
import TypeChip, { VOUCHER_TYPE_STYLES, getVoucherTypeLabel } from './TypeChip';
import VoucherDrawer from './VoucherDrawer';
import {
  useVoucherList,
  useLedgerAccounts,
  VOUCHER_TYPES,
  type Voucher,
  type VoucherListParams,
} from '@/hooks/useVouchers';

// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_OPTIONS = ['draft', 'posted', 'cancelled', 'amended'] as const;
type StatusKey = (typeof STATUS_OPTIONS)[number];

/** Distinct type-keys; styles map covers legacy aliases but the chip palette
 *  should show the canonical 20 from Sprint 2-3. */
const FILTER_TYPES: string[] = [
  'SALES_INVOICE',
  'PURCHASE_INVOICE',
  'PAYMENT',
  'RECEIPT',
  'JOURNAL',
  'CONTRA',
  'BANK_ENTRY',
  'CASH_ENTRY',
  'CREDIT_CARD_ENTRY',
  'INTER_COMPANY',
  'EXCISE',
  'WRITE_OFF',
  'OPENING',
  'DEPRECIATION',
  'EXCHANGE_RATE_REVALUATION',
  'EXCHANGE_GAIN_LOSS',
  'DEFERRED_REVENUE',
  'DEFERRED_EXPENSE',
  'ITC_REVERSAL',
  'ITC_RECLAIM',
];

const DATE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'fy', label: 'This FY' },
  { key: 'prevFy', label: 'Last FY' },
  { key: 'all', label: 'All' },
  { key: 'custom', label: 'Custom' },
] as const;
type DatePreset = (typeof DATE_PRESETS)[number]['key'];

const STATUS_STYLE: Record<string, { bg: string; text: string; border: string; strike: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300', strike: '' },
  posted: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-800',
    border: 'border-emerald-300',
    strike: '',
  },
  cancelled: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-300',
    strike: 'line-through',
  },
  amended: {
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    border: 'border-amber-300',
    strike: '',
  },
};

const PAGE_SIZE = 100;

// ─── Date utilities ─────────────────────────────────────────────────────────

function isoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fyBoundaries(offset: 0 | -1 = 0): { start: string; end: string } {
  // Indian FY: Apr 1 → Mar 31. offset=-1 → previous FY.
  const now = new Date();
  const m = now.getMonth(); // 0-indexed
  const fyStartYear = (m >= 3 ? now.getFullYear() : now.getFullYear() - 1) + offset;
  const start = new Date(fyStartYear, 3, 1);
  const end = new Date(fyStartYear + 1, 2, 31);
  return { start: isoDate(start), end: isoDate(end) };
}

function presetDates(preset: DatePreset): { start: string; end: string } | null {
  const today = new Date();
  switch (preset) {
    case 'today':
      return { start: isoDate(today), end: isoDate(today) };
    case 'week': {
      const day = today.getDay(); // 0=Sun .. 6=Sat
      const monday = new Date(today);
      monday.setDate(today.getDate() - ((day + 6) % 7));
      return { start: isoDate(monday), end: isoDate(today) };
    }
    case 'month': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: isoDate(first), end: isoDate(today) };
    }
    case 'fy':
      return fyBoundaries(0);
    case 'prevFy':
      return fyBoundaries(-1);
    case 'all':
      return null;
    case 'custom':
    default:
      return null;
  }
}

// ─── CSV export ─────────────────────────────────────────────────────────────

function csvEscape(v: unknown): string {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(rows: Voucher[], filename: string) {
  const header = [
    'Date',
    'Voucher #',
    'Type',
    'Narration',
    'Reference',
    'Status',
    'Debit',
    'Credit',
  ];
  const lines = [header.join(',')];
  rows.forEach((v) => {
    lines.push(
      [
        v.date,
        v.number,
        v.type,
        v.narration ?? '',
        v.reference ?? '',
        v.status,
        Number(v.totalDebit) || 0,
        Number(v.totalCredit) || 0,
      ]
        .map(csvEscape)
        .join(','),
    );
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Main component ─────────────────────────────────────────────────────────

type SortKey = 'date' | 'number' | 'type' | 'debit' | 'credit' | 'status';

export const VoucherRegister: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialOpenId = searchParams.get('voucher');

  // Filter state
  const [activeTypes, setActiveTypes] = useState<Set<string>>(new Set(FILTER_TYPES));
  const [activeStatuses, setActiveStatuses] = useState<Set<StatusKey>>(
    new Set(['draft', 'posted', 'amended']),
  );
  const [datePreset, setDatePreset] = useState<DatePreset>('fy');
  const [customStart, setCustomStart] = useState<string>('');
  const [customEnd, setCustomEnd] = useState<string>('');
  const [accountId, setAccountId] = useState<string>('');
  const [accountSearch, setAccountSearch] = useState<string>('');
  const [showAccountList, setShowAccountList] = useState(false);
  const [partySearch, setPartySearch] = useState<string>('');

  // Sorting
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [page, setPage] = useState(0);

  // Drawer
  const [openVoucher, setOpenVoucher] = useState<Voucher | null>(null);
  const [openVoucherId, setOpenVoucherId] = useState<string | null>(initialOpenId);

  // Refs for keyboard nav
  const filterRef = useRef<HTMLInputElement | null>(null);
  const [focusIndex, setFocusIndex] = useState(0);

  // Derive date range
  const dateRange = useMemo(() => {
    if (datePreset === 'custom') {
      return { start: customStart || undefined, end: customEnd || undefined };
    }
    const p = presetDates(datePreset);
    return p ? { start: p.start, end: p.end } : { start: undefined, end: undefined };
  }, [datePreset, customStart, customEnd]);

  // API params
  const listParams: VoucherListParams = useMemo(() => {
    const params: VoucherListParams = { limit: 1000 };
    if (activeTypes.size > 0 && activeTypes.size < FILTER_TYPES.length) {
      params.type = Array.from(activeTypes);
    }
    if (activeStatuses.size > 0 && activeStatuses.size < STATUS_OPTIONS.length) {
      params.status = Array.from(activeStatuses);
    }
    if (dateRange.start) params.startDate = dateRange.start;
    if (dateRange.end) params.endDate = dateRange.end;
    if (accountId) params.accountId = accountId;
    if (partySearch.trim()) params.search = partySearch.trim();
    return params;
  }, [activeTypes, activeStatuses, dateRange, accountId, partySearch]);

  const { vouchers, loading, error, refetch } = useVoucherList(listParams);
  const { options: ledgerOptions } = useLedgerAccounts(accountSearch);

  // Client-side sort
  const sortedVouchers = useMemo(() => {
    const arr = [...vouchers];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av: number | string = '';
      let bv: number | string = '';
      switch (sortKey) {
        case 'date':
          av = a.date;
          bv = b.date;
          break;
        case 'number':
          av = a.number;
          bv = b.number;
          break;
        case 'type':
          av = a.type;
          bv = b.type;
          break;
        case 'debit':
          av = Number(a.totalDebit) || 0;
          bv = Number(b.totalDebit) || 0;
          break;
        case 'credit':
          av = Number(a.totalCredit) || 0;
          bv = Number(b.totalCredit) || 0;
          break;
        case 'status':
          av = a.status;
          bv = b.status;
          break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }, [vouchers, sortKey, sortDir]);

  // Pagination slice
  const pagedVouchers = useMemo(
    () => sortedVouchers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [sortedVouchers, page],
  );
  const totalPages = Math.max(1, Math.ceil(sortedVouchers.length / PAGE_SIZE));

  // Aggregate sums
  const totals = useMemo(
    () =>
      sortedVouchers.reduce(
        (acc, v) => {
          acc.debit += Number(v.totalDebit) || 0;
          acc.credit += Number(v.totalCredit) || 0;
          return acc;
        },
        { debit: 0, credit: 0 },
      ),
    [sortedVouchers],
  );

  // Reset focusIndex when paged set changes
  useEffect(() => {
    setFocusIndex(0);
  }, [pagedVouchers.length, page]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [activeTypes, activeStatuses, dateRange.start, dateRange.end, accountId, partySearch]);

  // Sync drawer voucher id ↔ URL
  useEffect(() => {
    if (openVoucherId) searchParams.set('voucher', openVoucherId);
    else searchParams.delete('voucher');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openVoucherId]);

  // Keyboard nav: J=next, K=prev, F=focus filter, Enter=open drawer, Esc handled in drawer
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea.
      const t = e.target as HTMLElement | null;
      const tag = (t?.tagName ?? '').toUpperCase();
      const editing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable;

      if (e.key === 'f' || e.key === 'F') {
        if (!editing) {
          e.preventDefault();
          filterRef.current?.focus();
        }
        return;
      }
      if (editing) return; // J/K/Enter only when not typing

      if (e.key === 'j' || e.key === 'J' || e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIndex((i) => Math.min(i + 1, Math.max(0, pagedVouchers.length - 1)));
      } else if (e.key === 'k' || e.key === 'K' || e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIndex((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        const v = pagedVouchers[focusIndex];
        if (v) {
          e.preventDefault();
          setOpenVoucher(v);
          setOpenVoucherId(v.id);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pagedVouchers, focusIndex]);

  // Handlers
  const toggleType = (t: string) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };
  const allTypesOn = () => setActiveTypes(new Set(FILTER_TYPES));
  const clearTypes = () => setActiveTypes(new Set());
  const toggleStatus = (s: StatusKey) => {
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };
  const toggleSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir(k === 'date' || k === 'debit' || k === 'credit' ? 'desc' : 'asc');
    }
  };
  const openRow = (v: Voucher) => {
    setOpenVoucher(v);
    setOpenVoucherId(v.id);
  };
  const closeDrawer = useCallback(() => {
    setOpenVoucher(null);
    setOpenVoucherId(null);
  }, []);

  const fy = getFinancialYear();

  return (
    <div className="flex h-full flex-col bg-gray-50" data-testid="voucher-register-page">
      <style>{`
        @media print {
          /* Hide app chrome + register filter bar; show only the table. */
          body * { visibility: hidden; }
          .voucher-register-print, .voucher-register-print * { visibility: visible; }
          .voucher-register-print { position: absolute; left: 0; top: 0; width: 100%; }
          .print-hide { display: none !important; }
          aside[role="dialog"] { display: none !important; }
          table thead { display: table-header-group; }
          table tfoot { display: table-footer-group; }
        }
        @page { size: A4 portrait; margin: 15mm; }
      `}</style>

      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-6 py-4 print-hide">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Voucher Register</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {fy} (Apr-Mar) · {sortedVouchers.length} vouchers ·{' '}
              <span className="text-gray-400">
                Keys: <kbd className="rounded border border-gray-300 bg-gray-50 px-1 text-[10px]">J</kbd>
                /
                <kbd className="rounded border border-gray-300 bg-gray-50 px-1 text-[10px]">K</kbd>{' '}
                nav ·{' '}
                <kbd className="rounded border border-gray-300 bg-gray-50 px-1 text-[10px]">F</kbd>{' '}
                filter ·{' '}
                <kbd className="rounded border border-gray-300 bg-gray-50 px-1 text-[10px]">Enter</kbd>{' '}
                open
              </span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="voucher-register-refresh-button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              type="button"
              data-testid="voucher-register-csv-button"
              onClick={() =>
                downloadCsv(sortedVouchers, `voucher-register-${isoDate(new Date())}.csv`)
              }
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" /> CSV
            </button>
            <button
              type="button"
              data-testid="voucher-register-print-button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <Printer className="h-3.5 w-3.5" /> Print / PDF
            </button>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="border-b border-gray-200 bg-white px-6 py-3 print-hide">
        {/* Row 1 — search + date + account + party + status */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {/* Quick search */}
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              ref={filterRef}
              type="text"
              data-testid="voucher-register-search-input"
              value={partySearch}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPartySearch(e.target.value)}
              placeholder="Search narration, reference, party…"
              className="w-72 rounded-md border border-gray-200 bg-white py-1.5 pl-7 pr-2 text-xs focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
            />
          </div>

          {/* Date preset */}
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5 text-gray-400" />
            <select
              data-testid="voucher-register-date-preset-select"
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value as DatePreset)}
              className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs focus:border-gray-400 focus:outline-none"
            >
              {DATE_PRESETS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          {datePreset === 'custom' ? (
            <>
              <input
                type="date"
                data-testid="voucher-register-custom-start-input"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs"
              />
              <span className="text-xs text-gray-400">to</span>
              <input
                type="date"
                data-testid="voucher-register-custom-end-input"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs"
              />
            </>
          ) : null}

          {/* Account autocomplete */}
          <div className="relative">
            <input
              type="text"
              data-testid="voucher-register-account-search-input"
              value={accountSearch}
              onChange={(e) => {
                setAccountSearch(e.target.value);
                setShowAccountList(true);
              }}
              onFocus={() => setShowAccountList(true)}
              onBlur={() => setTimeout(() => setShowAccountList(false), 120)}
              placeholder="Filter by ledger account…"
              className="w-56 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs focus:border-gray-400 focus:outline-none"
            />
            {accountId ? (
              <button
                type="button"
                onClick={() => {
                  setAccountId('');
                  setAccountSearch('');
                }}
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600"
                aria-label="Clear account filter"
              >
                <X className="h-3 w-3" />
              </button>
            ) : null}
            {showAccountList && ledgerOptions.length > 0 ? (
              <div className="absolute z-20 mt-1 max-h-56 w-72 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                {ledgerOptions
                  .filter((o) =>
                    accountSearch
                      ? o.name.toLowerCase().includes(accountSearch.toLowerCase())
                      : true,
                  )
                  .slice(0, 30)
                  .map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      data-testid={`voucher-register-account-option-${o.id}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setAccountId(o.id);
                        setAccountSearch(o.name);
                        setShowAccountList(false);
                      }}
                      className="block w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50"
                    >
                      <span className="font-medium text-gray-800">{o.name}</span>
                      {o.code ? (
                        <span className="ml-2 text-gray-400">{o.code}</span>
                      ) : null}
                      {o.group?.nature ? (
                        <span className="ml-2 text-[10px] uppercase text-gray-400">
                          {o.group.nature}
                        </span>
                      ) : null}
                    </button>
                  ))}
              </div>
            ) : null}
          </div>

          {/* Status checkboxes */}
          <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1">
            {STATUS_OPTIONS.map((s) => (
              <label
                key={s}
                className="inline-flex cursor-pointer items-center gap-1 text-[11px] text-gray-600"
              >
                <input
                  type="checkbox"
                  data-testid={`voucher-register-status-checkbox-${s}`}
                  checked={activeStatuses.has(s)}
                  onChange={() => toggleStatus(s)}
                  className="h-3 w-3 rounded border-gray-300"
                />
                <span className="capitalize">{s}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Row 2 — type chips */}
        <div className="flex flex-wrap items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-gray-400" />
          <span className="mr-1 text-[11px] uppercase tracking-wider text-gray-400">Types</span>
          <button
            type="button"
            onClick={allTypesOn}
            className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-500 hover:border-gray-300 hover:text-gray-700"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={clearTypes}
            className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-500 hover:border-gray-300 hover:text-gray-700"
          >
            Clear
          </button>
          {FILTER_TYPES.map((t) => (
            <TypeChip
              key={t}
              type={t}
              interactive
              active={activeTypes.has(t)}
              onClick={() => toggleType(t)}
            />
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="voucher-register-print flex-1 overflow-y-auto px-6 py-4">
        {error ? (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm" data-testid="vouchers-table">
            <thead className="sticky top-0 z-10 bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <Th label="Date" sortKey="date" current={sortKey} dir={sortDir} onSort={toggleSort} />
                <Th label="Voucher #" sortKey="number" current={sortKey} dir={sortDir} onSort={toggleSort} />
                <Th label="Type" sortKey="type" current={sortKey} dir={sortDir} onSort={toggleSort} />
                <th className="px-3 py-2">Narration</th>
                <Th
                  label="Debit"
                  sortKey="debit"
                  current={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                  align="right"
                />
                <Th
                  label="Credit"
                  sortKey="credit"
                  current={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                  align="right"
                />
                <Th
                  label="Status"
                  sortKey="status"
                  current={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                />
                <th className="px-3 py-2 print-hide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-3 py-10 text-center text-sm text-gray-400">
                    Loading vouchers…
                  </td>
                </tr>
              ) : pagedVouchers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-10 text-center text-sm text-gray-400">
                    No vouchers for the selected filters. Try expanding the date range or clearing
                    type chips.
                  </td>
                </tr>
              ) : (
                pagedVouchers.map((v, i) => {
                  const status = (v.status ?? '').toLowerCase();
                  const st = STATUS_STYLE[status] ?? STATUS_STYLE.draft!;
                  const isFocused = i === focusIndex;
                  return (
                    <tr
                      key={v.id}
                      data-testid={`voucher-row-${v.id}`}
                      onClick={() => openRow(v)}
                      className={`cursor-pointer border-t border-gray-100 hover:bg-gray-50 ${
                        isFocused ? 'bg-blue-50/40 ring-1 ring-blue-200 ring-inset' : ''
                      }`}
                    >
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-700">
                        {formatDate(v.date)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-900">
                        {v.number}
                      </td>
                      <td className="px-3 py-2">
                        <TypeChip type={v.type} />
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        <div
                          className="max-w-md truncate"
                          title={v.narration ?? undefined}
                        >
                          {v.narration ?? '—'}
                        </div>
                        {v.reference ? (
                          <div className="text-[10px] text-gray-400">Ref: {v.reference}</div>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-900">
                        {Number(v.totalDebit) > 0 ? formatINR(v.totalDebit) : '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-900">
                        {Number(v.totalCredit) > 0 ? formatINR(v.totalCredit) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${st.bg} ${st.text} ${st.border} ${st.strike}`}
                        >
                          {status || '—'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 print-hide">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRow(v);
                          }}
                          className="text-xs text-blue-700 hover:underline"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot className="border-t-2 border-gray-200 bg-gray-50 text-sm font-semibold">
              <tr>
                <td colSpan={4} className="px-3 py-2 text-gray-700">
                  {sortedVouchers.length} row{sortedVouchers.length === 1 ? '' : 's'} ·
                  {' '}Balance{' '}
                  <span
                    className={
                      Math.abs(totals.debit - totals.credit) < 0.01
                        ? 'text-emerald-700'
                        : 'text-red-700'
                    }
                  >
                    {formatINR(totals.debit - totals.credit)}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                  {formatINR(totals.debit)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                  {formatINR(totals.credit)}
                </td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 ? (
          <div className="mt-3 flex items-center justify-end gap-2 text-xs text-gray-500 print-hide">
            <button
              type="button"
              data-testid="voucher-register-pagination-prev-button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-md border border-gray-200 bg-white px-2 py-1 disabled:opacity-40"
            >
              Prev
            </button>
            <span>
              Page {page + 1} of {totalPages}
            </span>
            <button
              type="button"
              data-testid="voucher-register-pagination-next-button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-md border border-gray-200 bg-white px-2 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        ) : null}

        {/* Print footer */}
        <div className="hidden print:mt-4 print:block print:text-[10px] print:text-gray-500">
          Printed {formatDate(new Date())} · Voucher Register · {fy} · AURA
        </div>

        {/* Sub-page links */}
        <div className="mt-6 flex flex-wrap gap-2 text-xs print-hide">
          <Link
            to="/ledger/trial-balance"
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50"
          >
            Trial Balance
          </Link>
          <Link
            to="/ledger/profit-loss"
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50"
          >
            Profit & Loss
          </Link>
          <Link
            to="/ledger/balance-sheet"
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50"
          >
            Balance Sheet
          </Link>
        </div>
      </div>

      {/* Drawer */}
      <VoucherDrawer
        voucherId={openVoucherId}
        preview={openVoucher}
        onClose={closeDrawer}
      />
    </div>
  );
};

function Th({
  label,
  sortKey: k,
  current,
  dir,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = current === k;
  return (
    <th className={`px-3 py-2 ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 ${
          active ? 'text-gray-700' : 'text-gray-500'
        } hover:text-gray-700`}
      >
        {label}
        {active ? (
          dir === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        ) : null}
      </button>
    </th>
  );
}

// `getVoucherTypeLabel` is re-exported for any caller that wants the same
// label resolution used by the chip (e.g. CSV columns elsewhere).
export { getVoucherTypeLabel };
// Used at module scope to keep VOUCHER_TYPE_STYLES + VOUCHER_TYPES referenced
// for downstream callers without an unused-import warning.
export const _styleKeys = Object.keys(VOUCHER_TYPE_STYLES);
export const _allTypes = VOUCHER_TYPES;

export default VoucherRegister;
